import React, { useEffect, useState } from "react";
import Scheduler from "devextreme-react/scheduler";
import CustomStore from "devextreme/data/custom_store";
import { fetchAllAppointments, createAppointment, updateAppointment, deleteAppointment, fetchAllWorkcells } from "../api";
import { Box, Typography, CircularProgress, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import FullWidthLayout from "../components/FullWidthLayout";
import { useTranslation } from "react-i18next";
import {
  diffSchedulerDateTimeHours,
  formatLocalDate,
  formatSchedulerTime,
  parseAppointmentDates,
  parseSchedulerDateTime
} from "../utils/localDateTime";

const getColor = (i) => {
  const palette = [
    "#1e90ff", "#ff6347", "#32cd32", "#ffa500", "#8a2be2", "#ff1493", "#00ced1", "#ffd700"
  ];
  return palette[i % palette.length];
};

const getDateTime = (base, hour, minute) => {
  const date = parseSchedulerDateTime(base);
  date.setUTCHours(hour, minute, 0, 0);
  return date;
};

const WorkcellAppointmentsMonthPage = () => {
  const { workcell_id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [workcells, setWorkcells] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(parseSchedulerDateTime(formatLocalDate(new Date())));

  // Set selectedWorkcell from URL param
  const selectedWorkcell = workcell_id ? parseInt(workcell_id) : (workcells[0]?.id || "");
  const selectedWorkcellObj = workcells.find(w => w.id === selectedWorkcell);

  useEffect(() => {
    fetchAllWorkcells()
      .then(data => {
        // Only include workcells with has_oee true
        const filtered = data.filter(w => w.has_oee);
        const workcellList = filtered.map((w, i) => ({
          id: w.workcell_id,
          text: w.workcell_name,
          color: getColor(i)
        }));
        setWorkcells(workcellList);
        // If no workcell_id in URL, redirect to first workcell
        if (!workcell_id && workcellList.length > 0) {
          navigate(`/workcell-appointments-month/${workcellList[0].id}`, { replace: true });
        }
      })
      .finally(() => setLoading(false));
  }, [workcell_id, navigate]);

  const appointmentsStore = new CustomStore({
    key: "id",
    load: () =>
      fetchAllAppointments(selectedWorkcell).then(data => {
        console.log('[appointmentsStore] data:', data);
        return (data || []).map(parseAppointmentDates);
      }),
    insert: createAppointment,
    update: updateAppointment,
    remove: deleteAppointment,
  });

  const handleAppointmentFormOpening = e => {
    // Use the cell's date if available
    const base = parseSchedulerDateTime(e.appointmentData.startDate);
    if (base.getUTCHours() === 0) {
      e.appointmentData.startDate = getDateTime(base, 8, 0);
      e.appointmentData.endDate = getDateTime(base, 16, 0);
    }
    if (!e.appointmentData.workcellId && selectedWorkcell) {
      e.appointmentData.workcellId = selectedWorkcell;
    }

    // hide title and description and workcell field
    // const orig = e.form.option('items');
    // const mainGroup = orig.find(item => item.name === 'mainGroup');
    // mainGroup.items = mainGroup.items.filter(item => item.name !== 'text' && item.name !== 'description' && item.dataField !== 'workcellId');
    // e.form.option('items', orig);
    e.form.option('formData', e.appointmentData);
  };

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress /></Box>;

  return (
    <FullWidthLayout>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="stretch"
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto'
        }}
      >
        <Box display="flex" alignItems="center" mb={2} mt={1} style={{ position: "relative" }}>
          <FormControl sx={{ minWidth: 260, mr: 2 }}>
            <InputLabel id="workcell-select-label">{t("workcell.selector.label")}</InputLabel>
            <Select
              labelId="workcell-select-label"
              value={selectedWorkcell || ""}
              label={t("workcell.selector.label")}
              onChange={e => navigate(`/workcell-appointments-month/${e.target.value}`)}
            >
              {workcells.map(w => (
                <MenuItem key={w.id} value={w.id} style={{ color: w.color }}>{w.text}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="h5">{t("workcellAppointmentsMonth.title")}</Typography>
          <Box ml={2} style={{ position: 'absolute', right: 0, top: 0, marginTop: "0" }}>
            <a href="/workcell-appointments" style={{ textDecoration: 'none' }}>
              <button style={{
                background: '#1976d2',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                padding: '6px 16px',
                fontSize: '1rem',
                cursor: 'pointer',
                fontWeight: 500
              }}>
                {t("workcellAppointmentsMonth.dailyView")}
              </button>
            </a>
          </Box>
        </Box>



        <Box flex={1}>
          <Scheduler
            dataSource={appointmentsStore}
            views={["month"]}
            currentView="month"
            currentDate={currentDate}
            onCurrentDateChange={setCurrentDate}
            timeZone="UTC"
            onAppointmentFormOpening={handleAppointmentFormOpening}
            width="100%"
            height="100%"
            editing={{
              allowAdding: true,
              allowUpdating: true,
              allowDeleting: true,
              allowResizing: true,
              allowDragging: true,
              useConfirmationDialog: false
            }}
            resources={[{
              fieldExpr: "workcellId",
              dataSource: selectedWorkcellObj ? [selectedWorkcellObj] : [],
              label: t("workcell.selector.label"),
              useColorAsDefault: true,
              colorExpr: "color"
            }]}
            group={{ resources: ['workcellId'] }}
            recurrenceRuleExpr="recurrenceRule"
            appointmentRender={({ appointmentData }) => {
              const workcell = selectedWorkcellObj;
              const startHour = formatSchedulerTime(appointmentData.startDate);
              const durationHours = diffSchedulerDateTimeHours(appointmentData.startDate, appointmentData.endDate);
              const durationH = Math.floor(durationHours);
              const durationM = Math.round((durationHours - durationH) * 60);
              const durationStr = durationH > 0
                ? (durationM > 0
                    ? t("workcellAppointmentsMonth.duration.hoursMinutes", { hours: durationH, minutes: durationM })
                    : t("workcellAppointmentsMonth.duration.hoursOnly", { hours: durationH }))
                : t("workcellAppointmentsMonth.duration.minutesOnly", { minutes: durationM });
              return (
                <div style={{
                  background: workcell?.color || "#1e90ff",
                  color: "#fff",
                  padding: 4,
                  borderRadius: 4,
                  height: "100%",
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  fontSize: "0.95em",
                  textAlign: "center"
                }}>
                  <div>{startHour} ({durationStr})</div>
                </div>
              );
            }}
            showAllDayPanel={false}
          />
        </Box>
      </Box>
    </FullWidthLayout>
  );
};

export default WorkcellAppointmentsMonthPage;
